import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { Select, ofActionSuccessful } from '@ngxs/store';
import { TranslateModule } from '@ngx-translate/core';
import { Observable, combineLatest, filter, map, merge, take, takeUntil, withLatestFrom } from 'rxjs';

import { ImportDataResultComponent } from '@app/pages/import-data-result/import-data-result.component';
import { PageBasedComponent } from '@app/pages/page.based.component';
import { LoadingSpinnerComponent } from '@app/shared/components/loading-spinner/loading-spinner.component';
import { MessageDataComponent } from '@app/shared/components/message-data/message-data.component';
import { DataGridComponent } from '@app/shared/components/smart-data-grid/data-grid/data-grid.component';
import { SmartSearchFormComponent } from '@app/shared/components/smart-search-form/smart-search-form.component';
import { SpinnerButtonComponent } from '@app/shared/components/spinner-button/spinner-button.component';
import { NgxsActionName, NgxsFormPath } from '@app/shared/constants';
import { KpiOthers } from '@app/shared/classes/kpi-others';
import { SearchByItem, WebApiResponse, WebApiResponseBase } from '@app/shared/classes/general';
import { AppDataState } from '@app/shared/stores/app-data.state';
import * as AppDataAction from '@app/shared/actions/app-data.action';
import { CommonModalDialogComponent } from '@app/shared/components/common-modal-dialog/common-modal-dialog.component';
import { CommonModalDialogOption, ModalDialogButtonType, ModalDialogResult } from '@app/shared/components/common-modal-dialog/common-modal-dialog.constant';
import { DataGridColumnDef, GetOthersSearchByList } from './others.constant';
import { OthersDataGridCell } from '@app/shared/components/smart-data-grid/data-grid-cell-templates/others-data-grid-cell.component';
import { OthersEditModalDialogComponent } from '@app/shared/components/edit-modal-dialog/others-edit-modal-dialog/others-edit-modal-dialog.component';
import { CommonPageFilterSetting, CommonSearchCriteria } from '@app/shared/models/common-data.model';
import { SiteConfigHelper } from '@app/shared/utils/site-config.helper';
import { DisableDeleteButtonDirective } from '@app/shared/directive/disable-delete-button.directive';
import { DisableEditButtonDirective } from '@app/shared/directive/disable-edit-button.directive';

@Component({
  selector: 'app-Others',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [LoadingSpinnerComponent, DataGridComponent, MessageDataComponent, CommonModule, TranslateModule, DisableDeleteButtonDirective, DisableEditButtonDirective,
    SmartSearchFormComponent, SpinnerButtonComponent, ImportDataResultComponent, OthersDataGridCell],
  templateUrl: './others.component.html',
  styleUrl: './others.component.css'
})
export class OthersComponent extends PageBasedComponent {
  dataGridColumnDef = DataGridColumnDef;
  formPath = NgxsFormPath.KPI_OTHERS_SEARCH_FORM;
  selectedItems: KpiOthers[] = [];

  dataSource$: Observable<KpiOthers[]>;
  OthersSearchByList$: Observable<SearchByItem[]>;

  @Select(AppDataState.currentKpiOthersSearchBy) currentKpiOthersSearchBy$: Observable<CommonSearchCriteria[]>;
  @Select(AppDataState.currentPageFilterSetting(NgxsActionName.GET_KPI_OTHERS)) currentPageFilterSetting$: Observable<CommonPageFilterSetting>;

  @Select(AppDataState.taskExecuting(NgxsActionName.GET_KPI_OTHERS)) searching$: Observable<boolean>;
  @Select(AppDataState.taskResponse(NgxsActionName.GET_KPI_OTHERS)) response$: Observable<WebApiResponse<KpiOthers[]>>;
  @Select(AppDataState.taskError(NgxsActionName.GET_KPI_OTHERS)) errorResponse$: Observable<WebApiResponseBase>;
  @Select(AppDataState.taskError(NgxsActionName.DELETE_KPI_OTHERS)) deleteErrorResponse$: Observable<WebApiResponseBase>;

  constructor() {
    super();
    this.functionName = NgxsActionName.GET_KPI_OTHERS;
  }

  override ngOnInit() {
    super.ngOnInit();
    this.currentKpiOthersSearchBy$.pipe(take(1)).subscribe(searchCriteria => this.defaultSearchCriteriaSub.next(searchCriteria));
    this.errorMessage$ = merge(this.commonDataLoadErrorMessage$, this.errorResponse$.pipe(map(errorResponse => this.getErrorMessages([errorResponse]))));

    this.dataSource$ = this.response$.pipe(filter(Boolean), map(c => c.data));
    this.totalCount$ = this.response$.pipe(filter(Boolean), map(c => c.totalCount));

    var kpis$ = this.kpiAllData$.pipe(filter(Boolean), map(c => c.data), filter(Boolean),
      map(kpi => kpi.filter(item => item.kpiCategory.kpiCategoryId === SiteConfigHelper.GetKpiCategoryIdMapping().Others).flatMap(item => item)));
    this.OthersSearchByList$ = combineLatest([this.currentTerminalDropDowns$, this.currentSubRegions$, kpis$, this.userCountry$]).pipe(map((c) => GetOthersSearchByList(c)));

    this.actions$.pipe(ofActionSuccessful(AppDataAction.DeleteKpiOthers), withLatestFrom(this.deleteErrorResponse$), takeUntil(this.destroy$)).subscribe(
      ([_, deleteErrorResponse]) => this.showResultDialog(deleteErrorResponse, "ml_143948", "ml_143949", this.onSearchWithExistedPageFilter));
  }

  rowSelected = (data: KpiOthers[]) => {
    this.selectedItems = data ?? [];
  }

  override onSearch = () => {
    this.selectedItems = [];
    this.store.dispatch(new AppDataAction.GetKpiOthers());
  }

  onSearchWithExistedPageFilter = () => {
    this.selectedItems = [];
    this.store.dispatch(new AppDataAction.GetKpiOthers(null));
  }

  onGetData = (info: CommonPageFilterSetting) => {
    info.functionName = NgxsActionName.GET_KPI_OTHERS;
    this.store.dispatch(new AppDataAction.GetKpiOthers(info));
  }

  onCreate = () => {
    this.dialog.open(OthersEditModalDialogComponent, {
      width: '700px',
      data: { successHandler: this.onSearchWithExistedPageFilter, isCreate: true },
      disableClose: true
    });
  }

  onEdit = () => {
    this.dialog.open(OthersEditModalDialogComponent, {
      width: '700px',
      data: { data: this.selectedItems[0], successHandler: this.onSearchWithExistedPageFilter },
      disableClose: true
    });
  }

  onDelete = () => {
    const dialogRef = this.dialog.open(CommonModalDialogComponent, {
      data: { header: true, message: "ml_143933", buttonType: ModalDialogButtonType.YES_NO } as CommonModalDialogOption,
      disableClose: false
    });

    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe(result => {
      if (result.data !== ModalDialogResult.YES) {
        return;
      }

      this.store.dispatch(new AppDataAction.DeleteKpiOthers(this.selectedItems.map(item => item.kpiOthers_Id)));
    })
  }
}
